// Copyright (c) StrangeBee 2024

import { useLocalMessage } from '@Helpers/locale';
import useValidateForm from '@Hooks/useValidateForm';
import { AuthEventList } from '@Organisms/AccountDetails/eventList';
import RouteBuilder from '@Services/RouteBuilder';
import { Button, Form, Input } from 'antd';
import { Store } from 'antd/lib/form/interface';
import React, { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import { ICONS } from '@Constants/iconsApp';

import './LoginPasswordReset.scss';

interface LoginPasswordResetProps {
    eventList: AuthEventList;
}

export const LoginPasswordReset = ({ eventList }: LoginPasswordResetProps): ReactElement => {
    const i = useLocalMessage();
    const history = useHistory();
    const { form, isValid, onFormValuesChange } = useValidateForm();

    const onFinish = (values: Store): void => {
        const { login } = values;
        if (login) {
            eventList.sendPasswordReset(login);
            history.push(RouteBuilder.compute('login.default'));
        }
    };

    return (
        <div className="login-password-reset">
            <div className="login-password-reset__title-container">
                <div className="login-password-reset__title">{i('login.reset.title')}</div>
                <div className="login-password-reset__subtitle">
                    {i('login.reset.forgot')} <br />
                    {i('login.reset.receive')}
                </div>
            </div>

            <div className="login-password-reset__form">
                <Form name="resetForm" onValuesChange={onFormValuesChange} form={form} onFinish={onFinish}>
                    <Form.Item
                        name="login"
                        rules={[
                            {
                                type: 'email',
                                required: true,
                                message: i('login.form.login.required'),
                            },
                        ]}
                    >
                        <Input
                            className="login-password-reset__item"
                            placeholder={i('login.form.login.placeholder')}
                            prefix={<ICONS.UserIcon className="icon" />}
                        />
                    </Form.Item>
                </Form>

                <div className="login-password-reset__actions">
                    <Button type="link" onClick={(): void => history.push(RouteBuilder.compute('login.default'))}>
                        {i('general.cancel')}
                    </Button>
                    <Button type="primary" disabled={!isValid} htmlType="submit" onClick={(): void => form.submit()}>
                        {i('general.send')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
